import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialColorOutput, materialFloatOutput, materialPointOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {VoronoiTextureDistance, VoronoiTextureFeature} from "#material/nodes/texture/tex-voronoi/types"
import {voronoiTextureDistanceSchema, voronoiTextureFeatureSchema} from "#material/nodes/texture/tex-voronoi/types"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {renameF4ToF1} from "#material/nodes/texture/tex-voronoi/validation"
import {versionChain, color} from "@cm/graph-v3"
import {vec3} from "#material/types/vector"
import {z} from "zod/v4-mini"
import {keepLegacyColorMapping, keepLegacyTextureMapping} from "#material/nodes/texture/legacy-texture-settings"
import type {TexNoiseDimensions} from "#material/nodes/texture/tex-noise/types"
import {texNoiseDimensionsSchema} from "#material/nodes/texture/tex-noise/types"

export interface TexVoronoiSockets extends MaterialNodeSockets {
    inputs: {
        color: "color" // found in inputs: -; found in settings: color; inferred from data: float3
        exponent: "float" // found in inputs: -; found in settings: float; inferred from data: float
        position: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
        radius: "float" // found in inputs: -; found in settings: float; inferred from data: float
        randomness: "float" // found in inputs: -; found in settings: float; inferred from data: float
        roughness: "float"
        scale: "float" // found in inputs: -; found in settings: float; inferred from data: float
        smoothness: "float" // found in inputs: -; found in settings: float; inferred from data: float
        vector: "point" // found in inputs: point; found in settings: vector; inferred from data: float3
        w: "float" // found in inputs: -; found in settings: float; inferred from data: float
    }
    settings: {
        color: "color" // found in inputs: -; found in settings: color; inferred from data: float3
        coloring: "string" // found in inputs: -; found in settings: string; inferred from data: string
        detail: "float"
        lacunarity: "float"
        normalize: "boolean" // found in inputs: -; found in settings: string,float; inferred from data: string,float
        exponent: "float" // found in inputs: -; found in settings: float; inferred from data: float
        fac: "float" // found in inputs: -; found in settings: float; inferred from data: float
        position: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
        radius: "float" // found in inputs: -; found in settings: float; inferred from data: float
        randomness: "float" // found in inputs: -; found in settings: float; inferred from data: float
        roughness: "float"
        scale: "float" // found in inputs: -; found in settings: float; inferred from data: float
        smoothness: "float" // found in inputs: -; found in settings: float; inferred from data: float
        vector: "point" // found in inputs: point; found in settings: vector; inferred from data: float3
        w: "float" // found in inputs: -; found in settings: float; inferred from data: float
    }
    settingsExtensions: {
        distance?: VoronoiTextureDistance
        feature?: VoronoiTextureFeature // found in inputs: -; found in settings: string; inferred from data: string
        voronoiDimensions?: TexNoiseDimensions // found in inputs: -; found in settings: string; inferred from data: string
    }
    outputs: {
        distance: "float"
        color: "color"
        position: "point"
    }
}

const SMOOTH_ONLY = {setting: "feature", oneOf: ["SMOOTH_F1"]} as const
const HAS_FRACTAL_DETAIL = {setting: "feature", oneOf: [undefined, "F1", "F2", "SMOOTH_F1", "DISTANCE_TO_EDGE"]} as const
// one octave takes no shaping, so these two only say anything once the feature fractures at all
const SHAPES_THE_OCTAVES = [HAS_FRACTAL_DETAIL, {setting: "detail", whenSet: true}] as const
const HAS_W = {setting: "voronoiDimensions", oneOf: ["1D", "4D"]} as const
const HAS_VECTOR = {setting: "voronoiDimensions", oneOf: [undefined, "2D", "3D", "4D"]} as const

export const texVoronoiDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeTexVoronoi,
    },
    nodeClass: "TexVoronoi",
    versionChain: versionChain([renameF4ToF1]),
    ui: {
        label: "Voronoi Texture",
        color: "#4987af",
        items: [
            {name: "distance", valueType: "float", label: "Distance", output: true},
            {name: "color", valueType: "color", label: "Value", output: true},
            {name: "position", valueType: "point", label: "Position", output: true},
            {name: "vector", valueType: "point", label: "Vector", input: true, defaultValue: {x: 0, y: 0, z: 0}, visibleWhen: HAS_VECTOR},
            {name: "w", valueType: "float", label: "W", input: true, setting: true, defaultValue: 0, visibleWhen: HAS_W},
            {name: "scale", valueType: "float", label: "Scale", input: true, setting: true, defaultValue: 5},
            {name: "detail", valueType: "float", label: "Detail", input: false, setting: true, defaultValue: 0, visibleWhen: HAS_FRACTAL_DETAIL},
            {
                name: "roughness",
                valueType: "float",
                label: "Roughness",
                input: true,
                range: {min: 0, max: 1},
                setting: true,
                defaultValue: 0.5,
                visibleWhen: SHAPES_THE_OCTAVES,
            },
            {name: "lacunarity", valueType: "float", label: "Lacunarity", input: false, setting: true, defaultValue: 2, visibleWhen: SHAPES_THE_OCTAVES},
            {name: "smoothness", valueType: "float", label: "Smoothness", input: true, setting: true, defaultValue: 5, visibleWhen: SMOOTH_ONLY},
            {name: "randomness", valueType: "float", label: "Randomness", input: true, setting: true, defaultValue: 1},
            {
                name: "voronoiDimensions",
                defaultValue: "3D",
                label: "Dimensions",
                valueType: "string",
                options: [
                    {label: "1D", value: "1D"},
                    {label: "2D", value: "2D"},
                    {label: "3D", value: "3D"},
                    {label: "4D", value: "4D"},
                ],
                input: false,
                setting: true,
            },
            {
                name: "feature",
                defaultValue: "F1",
                label: "Feature",
                valueType: "string",
                options: [
                    {label: "F1", value: "F1"},
                    {label: "F2", value: "F2"},
                    {label: "Smooth F1", value: "SMOOTH_F1"},
                    {label: "Distance To Edge", value: "DISTANCE_TO_EDGE"},
                    {label: "N-Sphere Radius", value: "N_SPHERE_RADIUS"},
                ],
                input: false,
                setting: true,
            },
            {
                name: "distance",
                defaultValue: "EUCLIDEAN",
                label: "Distance",
                valueType: "string",
                options: [
                    {label: "Euclidean", value: "EUCLIDEAN"},
                    {label: "Manhattan", value: "MANHATTAN"},
                    {label: "Chebychev", value: "CHEBYCHEV"},
                    {label: "Minkowski", value: "MINKOWSKI"},
                ],
                input: false,
                setting: true,
            },
            {name: "normalize", label: "Normalize", valueType: "boolean", defaultValue: false, input: false, setting: true},
        ],
        support: {
            cycles: {
                full: false,
                remark: "Some parameters unimplemented.",
            },
            three: false,
        },
    },
    validation: {
        returns: z.strictObject({distance: materialFloatOutput, color: materialColorOutput, position: materialPointOutput}),
        parameters: z.strictObject({
            inputs: z.strictObject({
                color: optionalMaterialInput,
                exponent: optionalMaterialInput,
                position: optionalMaterialInput,
                radius: optionalMaterialInput,
                randomness: optionalMaterialInput,
                roughness: optionalMaterialInput,
                scale: optionalMaterialInput,
                smoothness: optionalMaterialInput,
                vector: optionalMaterialInput,
                w: optionalMaterialInput,
            }),
            settings: z.strictObject({
                color: z.optional(color),
                ...keepLegacyColorMapping,
                coloring: z.optional(z.string()),
                detail: z.optional(z.number()), // legacy parameter
                distance: z.optional(voronoiTextureDistanceSchema),
                exponent: z.optional(z.number()),
                fac: z.optional(z.number()),
                feature: z.optional(voronoiTextureFeatureSchema),
                gradientType: z.optional(z.string()),
                lacunarity: z.optional(z.number()), // legacy parameter
                normalize: z.optional(z.boolean()), // legacy parameter
                position: z.optional(vec3),
                radius: z.optional(z.number()),
                randomness: z.optional(z.number()),
                roughness: z.optional(z.number()),
                scale: z.optional(z.number()),
                smoothness: z.optional(z.number()),
                ...keepLegacyTextureMapping,
                vector: z.optional(vec3),
                voronoiDimensions: z.optional(texNoiseDimensionsSchema),
                w: z.optional(z.number()),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<TexVoronoiSockets>
